import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  getProfileDetail,
  getProfileCompletion,
  updateProfile,
  patchProfile,
} from "../api/profile.api";
import type { ProfilePayload } from "@/interface/profile";
import {
  authKeys,
  profileKeys,
  candidateProfileKeys,
  STALE_5_MIN,
} from "@/lib/queryKey";

export const useProfileDetail = () =>
  useQuery({
    queryKey: authKeys.me(),
    queryFn: getProfileDetail,
    ...STALE_5_MIN,
  });

export const useProfileCompletion = (id?: number | string, enabled = true) =>
  useQuery({
    queryKey: ["profile-completion", String(id)],
    queryFn: () => getProfileCompletion(id!),
    enabled: Boolean(id) && enabled,
    ...STALE_5_MIN,
  });

export const useUpdateProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: ProfilePayload;
    }) => updateProfile(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.detail(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
      queryClient.invalidateQueries({ queryKey: ["myProfiles"] });
    },
  });
};

export const usePatchProfile = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: number | string;
      payload: Partial<ProfilePayload> | FormData;
    }) => patchProfile(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: authKeys.me() });
      queryClient.invalidateQueries({
        queryKey: candidateProfileKeys.detail(),
      });
      queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
    },
  });
};
